import type { LayerKey } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { isEnumMetaType } from '../../types/metaModel'
import { getLiteralName } from '../../uiModels/enumLiterals/helpers'
import {
  ALL_VERSIONS_VIEW_DATE,
  getApplicationViewConfiguredEnums,
  getApplicationViewParameterDefinitions,
  getShadowedDefinitionLayer,
} from '../../workspace/helpers'
import { useOperationsContext } from '../../workspace/operationsContext'
import { usePathSelectorValidations } from '../draftLocationSelectors/usePathSelectorValidations'
import type { CreateParameterFormData } from './types'

type Props = {
  layer: LayerKey
  block: string
}

export const useParameterDefinitionDraftValidationSchema = ({
  layer,
  block,
}: Props) => {
  const { parameterDefinitionCreateOperations, configuredEnums } =
    useOperationsContext()
  const translate = useTranslate()
  const { layerTopology, blockTopology, getLayerMetaModel } =
    useMetaModelContext()
  const pathSelectorValidations = usePathSelectorValidations()
  const translateLayer = useTranslateLayer()
  const parameterDefinitionsForBlockAndLayer = useMemo(
    () =>
      getApplicationViewParameterDefinitions({
        parameterDefinitionCreateOperations,
        layer,
        block,
        layerTopology,
        blockTopology,
        viewAt: ALL_VERSIONS_VIEW_DATE,
      }),
    [
      parameterDefinitionCreateOperations,
      layer,
      block,
      layerTopology,
      blockTopology,
    ],
  )
  const configuredEnumsForBlockAndLayer = useMemo(
    () =>
      getApplicationViewConfiguredEnums({
        configuredEnums,
        layer,
        block,
        layerTopology,
        blockTopology,
      }),
    [configuredEnums, layer, block, layerTopology, blockTopology],
  )
  const metaModel = useMemo(
    () => getLayerMetaModel(layer.layerId),
    [getLayerMetaModel, layer.layerId],
  )
  const modeledEnums = useMemo(
    () => Array.from(metaModel.types.values()).filter(isEnumMetaType),
    [metaModel.types],
  )
  const modeledParameterType = modeledEnums.find(
    (modeledEnum) => modeledEnum.typeName === 'ParameterType',
  )
  const modeledEnvironmentType = modeledEnums.find(
    (modeledEnum) => modeledEnum.typeName === 'ParameterEnvironmentType',
  )

  return Yup.object({
    parameterId: Yup.string()
      .min(2, translate('validation.length', { length: 2 }))
      .required(translate('validation.required'))
      .noSpaces(translate('validation.noSpaces'))
      .test('definedOnAnotherLayer', (value, context) => {
        const shadowedLayer = getShadowedDefinitionLayer({
          indexedOperations: value
            ? parameterDefinitionsForBlockAndLayer.get(value)
            : undefined,
          layer,
        })

        return (
          !shadowedLayer ||
          context.createError({
            message: translate('parameterDraftForm.validation.differentLayer', {
              parameterId: value,
              sourceLayer: translateLayer(shadowedLayer),
            }),
          })
        )
      }),
    fromDate: Yup.string()
      .test({
        test: (value) =>
          !value || !isNaN(dateUtils.dateUtcAsLocal(value!).valueOf()),
        message: translate('validation.invalidDate'),
      })
      .test(
        'nonExistentOperation',
        translate('validation.duplicatedOperation'),
        (value, context) => {
          const formInput = context.parent as Partial<CreateParameterFormData>
          const currentParameterId = formInput.parameterId
          const matchingOperation = currentParameterId
            ? parameterDefinitionsForBlockAndLayer.get(currentParameterId)
            : undefined

          return !(matchingOperation?.operations ?? []).some(
            (entry) =>
              entry.isDraft &&
              entry.internalOperation?.entity?.fromDate == value,
            // Purposely using == to allow for undefined and null to be treated the same, since the fromDate is optional
          )
        },
      ),
    environmentType: Yup.string()
      .required()
      .test(
        'validEnvironmentType',
        translate('validation.invalidParameterType'),
        (value) => {
          const environmentTypeDefinition = configuredEnumsForBlockAndLayer.get(
            'ParameterEnvironmentType',
          )

          return (
            (environmentTypeDefinition?.literals ?? [])
              .map(getLiteralName)
              .includes(value) ||
            modeledEnvironmentType?.values?.includes(value)
          )
        },
      ),
    parameterType: Yup.string()
      .required()
      .test(
        'validParameterType',
        translate('validation.invalidParameterType'),
        (value) => {
          const parameterTypeDefinition =
            configuredEnumsForBlockAndLayer.get('ParameterType')

          return (
            (parameterTypeDefinition?.literals ?? [])
              .map(getLiteralName)
              .includes(value) || modeledParameterType?.values?.includes(value)
          )
        },
      ),
    packagePath: pathSelectorValidations,
  })
}
